import { supabase } from './supabaseClient.js';
import { AI_ABORT_MS } from './aiTimeouts.js';

/**
 * Character Dynamics System
 *
 * Automatically generates behavioral analysis for character pairs by reading
 * Discord RP history and DM history. The analysis tells the AI HOW to behave
 * with a specific person — not just who the character is, but their emotional
 * patterns, deflection tactics, vocabulary, texting rhythm, and hard "never do" rules.
 *
 * Flow:
 * 1. When a DM opens, check if dynamics exist for this character+identity pair
 * 2. If not, gather source material (Discord RP + existing DMs)
 * 3. Send to AI with an analysis prompt
 * 4. Store the result in character_dynamics table
 * 5. Inject into system prompt on every subsequent message
 */

// ─── FETCH EXISTING DYNAMICS ────────────────────────────────────────────────

/**
 * Get stored dynamics for a character pair. Returns null if none exist.
 */
export async function getCharacterDynamics(characterId, targetCharacterId, targetIdentityType = 'self', userId = null) {
  try {
    let query = supabase
      .from('character_dynamics')
      .select('*')
      .eq('character_id', characterId)
      .eq('target_identity_type', targetIdentityType);

    if (targetCharacterId) {
      query = query.eq('target_character_id', targetCharacterId);
    } else {
      query = query.is('target_character_id', null);
    }

    // Dynamics are per-user (Haley's Ford relationship ≠ Nicole's). Prefer the
    // caller's own row; fall back to legacy rows that predate user scoping.
    if (userId) {
      query = query
        .or(`user_id.eq.${userId},user_id.is.null`)
        .order('user_id', { ascending: true, nullsFirst: false });
    } else {
      query = query.is('user_id', null);
    }

    const { data, error } = await query.limit(1);
    if (error) {
      console.error('Error fetching character dynamics:', error);
      return null;
    }
    return data?.[0] || null;
  } catch (err) {
    console.error('Error in getCharacterDynamics:', err);
    return null;
  }
}

// ─── BUILD DYNAMICS PROMPT SECTION ──────────────────────────────────────────

/**
 * Formats stored dynamics into a system prompt section.
 * This is injected between the character profile and the writing examples.
 */
export function formatDynamicsForPrompt(dynamics) {
  if (!dynamics) return '';

  // Dated (2026-10-19 audit): the prose is a one-shot analysis; its "now" is
  // the date it was written.
  const asOf = dynamics.updated_at ? String(dynamics.updated_at).slice(0, 10) : '';
  let section = `\nRELATIONSHIP DYNAMICS WITH ${(dynamics.target_name || 'this person').toUpperCase()}${asOf ? ` (as of ${asOf})` : ''} — FOLLOW THESE BEHAVIORAL RULES:\n`;

  if (dynamics.behavioral_summary) {
    section += `\n${dynamics.behavioral_summary}\n`;
  }

  if (dynamics.emotional_patterns) {
    section += `\nEMOTIONAL PATTERNS:\n${dynamics.emotional_patterns}\n`;
  }

  if (dynamics.texting_style) {
    section += `\nTEXTING STYLE:\n${dynamics.texting_style}\n`;
  }

  if (dynamics.vocabulary_notes) {
    section += `\nVOCABULARY & LANGUAGE:\n${dynamics.vocabulary_notes}\n`;
  }

  if (dynamics.relationship_stage) {
    section += `\nCURRENT RELATIONSHIP STAGE:\n${dynamics.relationship_stage}\n`;
  }

  if (dynamics.never_do) {
    section += `\nNEVER DO (HARD RULES — violating these is COMPLETELY out of character):\n${dynamics.never_do}\n`;
  }

  return section;
}

// ─── GATHER SOURCE MATERIAL ─────────────────────────────────────────────────

/**
 * Fetch Discord RP history that involves both characters in the pair.
 * Looks for channels named after either character, and messages where
 * both characters appear in the content or metadata.
 */
async function gatherDiscordHistory(characterName, targetName, limit = 200) {
  if (!characterName) return [];

  try {
    // Strategy 1: Find channels that reference BOTH characters
    const charLower = characterName.toLowerCase();
    const targetLower = targetName ? targetName.toLowerCase().split(' ')[0] : ''; // First name only

    // Get messages from channels named after either character
    let query = supabase
      .from('rp_history')
      .select('channel_name, content, author')
      .gt('content', '');

    // Search for channels with either character name
    if (targetName) {
      query = query.or(
        `channel_name.ilike.%${characterName}%,channel_name.ilike.%${targetLower}%`
      );
    } else {
      query = query.ilike('channel_name', `%${characterName}%`);
    }

    const { data, error } = await query.limit(600);

    if (error || !data || data.length === 0) return [];

    // Filter for substantial messages (meaningful dialogue/narration)
    let relevant = data.filter(msg => msg.content && msg.content.length > 80);

    // If we have a target name, prioritize messages that mention both characters
    if (targetName) {
      const bothMentioned = relevant.filter(msg => {
        const content = msg.content.toLowerCase();
        const channel = (msg.channel_name || '').toLowerCase();
        const hasChar = content.includes(charLower) || channel.includes(charLower);
        const hasTarget = content.includes(targetLower) || channel.includes(targetLower);
        return hasChar && hasTarget;
      });

      // Also get messages from channels named after their pairing
      const pairingChannels = relevant.filter(msg => {
        const ch = (msg.channel_name || '').toLowerCase();
        return (ch.includes(charLower) && ch.includes(targetLower)) ||
               ch.includes(`${charLower}-${targetLower}`) ||
               ch.includes(`${targetLower}-${charLower}`);
      });

      // Combine: pairing channels first, then both-mentioned, then general
      const combined = [...new Set([...pairingChannels, ...bothMentioned, ...relevant])];
      relevant = combined;
    }

    // Shuffle and take a diverse sample
    for (let i = relevant.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [relevant[i], relevant[j]] = [relevant[j], relevant[i]];
    }

    return relevant.slice(0, limit).map(msg => ({
      channel: msg.channel_name,
      author: msg.author,
      content: msg.content.substring(0, 800)
    }));
  } catch (err) {
    console.error('Error gathering Discord history:', err);
    return [];
  }
}

/**
 * Fetch existing DM messages between this character pair.
 */
async function gatherDMHistory(conversationId, limit = 100) {
  if (!conversationId) return [];

  try {
    const { data, error } = await supabase
      .from('dm_messages')
      .select('sender_type, content, created_at')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .limit(limit);

    if (error || !data) return [];

    return data.filter(m => m.content && m.content.length > 10).map(m => ({
      role: m.sender_type === 'user' ? 'user' : 'character',
      content: m.content.substring(0, 500)
    }));
  } catch (err) {
    console.error('Error gathering DM history:', err);
    return [];
  }
}

// ─── GENERATE DYNAMICS ANALYSIS ─────────────────────────────────────────────

/**
 * The analysis prompt that tells the AI what to extract from the source material.
 * This produces rich, specific behavioral rules — not vague descriptions.
 */
function buildAnalysisPrompt(characterName, targetName, _sourceType) {
  return `You are a character behavior analyst for a roleplay universe. You've been given actual RP writing samples showing how "${characterName}" behaves around "${targetName}".

Your job is to extract SPECIFIC, ACTIONABLE behavioral rules that another AI could follow to play ${characterName} authentically in conversations with ${targetName}.

Analyze the source material and produce the following sections. Be SPECIFIC — cite actual patterns you observe, not generic descriptions. If you see a pattern in the text, state it clearly as a rule.

FORMAT YOUR RESPONSE EXACTLY AS:

BEHAVIORAL_SUMMARY:
(2-4 paragraphs describing how ${characterName} specifically acts around ${targetName}. What's their dynamic? Who has the power? How do they handle tension, affection, vulnerability? What's their default emotional register? How do they react when ${targetName} pushes them? What do they do when they're caught off guard?)

EMOTIONAL_PATTERNS:
(How does ${characterName} handle emotions with ${targetName}? Do they deflect? Get flustered? Go cold? Use humor? What triggers different emotional responses? When do they crack vs. when do they stay guarded? How do they handle jealousy, worry, desire, anger?)

TEXTING_STYLE:
(Based on the examples: How long are their messages? Do they use emoji? What's their punctuation style? Do they double-text? How quickly do they respond? Do they send one long message or several short ones? Are they formal or casual? Do they use all-caps, lowercase only, fragments?)

VOCABULARY_NOTES:
(Specific words, phrases, pet names, slang, verbal tics they use with ${targetName}. What do they call them? What phrases are signature? What language do they NEVER use? Do they swear? Use terms of endearment? Are there words that are "theirs"?)

RELATIONSHIP_STAGE:
(Where is this relationship RIGHT NOW based on the material? Is it early tension, deep established bond, unspoken feelings, openly together, complicated? What's the current emotional temperature?)

NEVER_DO:
(CRITICAL: List specific things ${characterName} would NEVER do or say around ${targetName}. These are hard behavioral boundaries. E.g., "Never openly confess feelings first", "Never use the word 'love' directly", "Never be cruel even when angry", etc. Be very specific.)`;
}

/**
 * Generate dynamics analysis by sending source material to the AI.
 * Uses the same ai-proxy edge function as normal DM conversations.
 */
export async function generateDynamicsAnalysis(
  characterId,
  characterName,
  targetCharacterId,
  targetName,
  targetIdentityType,
  conversationId,
  aiProvider,
  accessToken,
  userId = null
) {
  // Gather source material
  const discordHistory = await gatherDiscordHistory(characterName, targetName, 150);
  const dmHistory = conversationId ? await gatherDMHistory(conversationId, 80) : [];

  const totalSources = discordHistory.length + dmHistory.length;
  if (totalSources < 5) {
    console.log(`Not enough source material for ${characterName}/${targetName} dynamics (found ${totalSources})`);
    return null;
  }

  // Build the source material text
  let sourceText = '';

  if (discordHistory.length > 0) {
    sourceText += `=== DISCORD RP HISTORY (${discordHistory.length} samples) ===\n`;
    sourceText += discordHistory.map(m =>
      `[${m.channel}] (${m.author}): ${m.content}`
    ).join('\n\n');
  }

  if (dmHistory.length > 0) {
    sourceText += `\n\n=== DM CONVERSATION HISTORY (${dmHistory.length} messages) ===\n`;
    sourceText += dmHistory.map(m =>
      `[${m.role}]: ${m.content}`
    ).join('\n');
  }

  // Build the analysis prompt
  const systemPrompt = buildAnalysisPrompt(characterName, targetName || 'this person', 'mixed');

  // Call AI via the same edge function
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
      method: 'POST',
      // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(AI_ABORT_MS),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        provider: aiProvider,
        systemPrompt,
        messages: [{ role: 'user', content: sourceText }],
        maxTokens: 800,
        feature: 'dynamics_analysis',
        nsfw: true, // analyzes RP exchanges which may contain NSFW
        characterId,
        characterName,
      }),
    });

    if (!res.ok) {
      console.error('Dynamics analysis API error:', res.status);
      return null;
    }

    const data = await res.json();
    const analysisText = data.response || data.content || data.text || '';

    if (!analysisText || analysisText.length < 100) {
      console.error('Dynamics analysis returned empty/short result');
      return null;
    }

    // Parse the structured response
    const parsed = parseAnalysisResponse(analysisText);

    // Store in database
    const dynamicsRow = {
      character_id: characterId,
      target_character_id: targetCharacterId || null,
      target_identity_type: targetIdentityType || 'self',
      target_name: targetName,
      behavioral_summary: parsed.behavioral_summary,
      emotional_patterns: parsed.emotional_patterns,
      texting_style: parsed.texting_style,
      vocabulary_notes: parsed.vocabulary_notes,
      relationship_stage: parsed.relationship_stage,
      never_do: parsed.never_do,
      source_message_count: totalSources,
      source_types: discordHistory.length > 0 && dmHistory.length > 0
        ? ['rp_history', 'dm_messages']
        : discordHistory.length > 0 ? ['rp_history'] : ['dm_messages'],
      generated_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user_id: userId || null,
      // Audit 2026-08-30 #28: the unique index is the six-column pair index —
      // the four-column onConflict matched NO index, so every upsert failed
      // 42P10 into a swallowed console.error and dynamics froze on 08-24.
      scope_type: null,
      scope_id: null,
    };

    const { data: inserted, error: insertError } = await supabase
      .from('character_dynamics')
      .upsert(dynamicsRow, {
        onConflict: 'character_id,target_character_id,target_identity_type,user_id,scope_type,scope_id'
      })
      .select()
      .single();

    if (insertError) {
      console.error('Error storing dynamics:', insertError);
      // Return the parsed data anyway so it can be used this session
      return dynamicsRow;
    }

    return inserted || dynamicsRow;
  } catch (err) {
    console.error('Error generating dynamics analysis:', err);
    return null;
  }
}

/**
 * Parse the AI's structured response into separate fields.
 */
function parseAnalysisResponse(text) {
  const sections = {
    behavioral_summary: '',
    emotional_patterns: '',
    texting_style: '',
    vocabulary_notes: '',
    relationship_stage: '',
    never_do: '',
  };

  const sectionMap = {
    'BEHAVIORAL_SUMMARY': 'behavioral_summary',
    'EMOTIONAL_PATTERNS': 'emotional_patterns',
    'TEXTING_STYLE': 'texting_style',
    'VOCABULARY_NOTES': 'vocabulary_notes',
    'RELATIONSHIP_STAGE': 'relationship_stage',
    'NEVER_DO': 'never_do',
  };

  // Split by section headers
  let currentSection = null;
  const lines = text.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    // Check if this line is a section header
    const headerMatch = trimmed.replace(':', '').toUpperCase();
    if (sectionMap[headerMatch]) {
      currentSection = sectionMap[headerMatch];
      continue;
    }
    // Also match "BEHAVIORAL SUMMARY:" format (with space)
    const spaceHeaderMatch = trimmed.replace(/[:\s_]+/g, '_').replace(/_$/, '').toUpperCase();
    if (sectionMap[spaceHeaderMatch]) {
      currentSection = sectionMap[spaceHeaderMatch];
      continue;
    }

    if (currentSection && trimmed) {
      sections[currentSection] += (sections[currentSection] ? '\n' : '') + trimmed;
    }
  }

  // Fallback: if parsing failed, put everything in behavioral_summary
  const totalContent = Object.values(sections).join('').trim();
  if (!totalContent && text.length > 50) {
    sections.behavioral_summary = text;
  }

  return sections;
}

// ─── STORY ARC SUMMARIZATION ────────────────────────────────────────────────

/**
 * Generate a story arc summary for a long DM conversation.
 * Uses the dedicated summarize-dm edge function (Sonnet-powered, server-side).
 * Falls back to client-side ai-proxy if the edge function fails.
 */
// summarize-dm needs an env ANTHROPIC_API_KEY that this BYOK-only platform
// deliberately does not set, so it answers 500 on every call. Flip this to
// true only after that function reads the caller's vault key instead.
const SUMMARIZE_DM_HAS_ITS_OWN_KEY = false;

export async function generateStoryArcSummary(
  conversationId,
  characterName,
  targetName,
  userId,
  aiProvider,
  accessToken
) {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    // summarize-dm IS BYPASSED, DELIBERATELY. It reads an env
    // ANTHROPIC_API_KEY, and this platform is BYOK-only — that secret is
    // intentionally unset (see health-check's note), so the call returned
    // 500 "Server misconfigured: ANTHROPIC_API_KEY missing" EVERY time and
    // fell through to the ai-proxy path below. Verified by invoking it
    // directly, 2026-09-28. Skipping it removes a guaranteed-failing round
    // trip and a console error that masked real signal; ai-proxy is also the
    // only path with the dialect layer, the budget rescue and cost logging.
    // To revive summarize-dm, give it the caller's vault key via
    // get_my_ai_config the way ai-proxy does — not an env secret.
    if (SUMMARIZE_DM_HAS_ITS_OWN_KEY) {
    const existingSummary = await getStoryArcSummary(conversationId);
    const res = await fetch(`${supabaseUrl}/functions/v1/summarize-dm`, {
      method: 'POST',
      // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(AI_ABORT_MS),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        conversationId,
        characterName,
        existingSummary: existingSummary?.summary_text || null,
      }),
    });

    if (!res.ok) {
      console.error('summarize-dm edge function error:', res.status);
      return await generateStoryArcSummaryFallback(conversationId, characterName, targetName, userId, aiProvider, accessToken);
    }

    const data = await res.json();

    // If not enough messages, the edge function returns a 200 with an error field
    if (data.error && !data.summary) {
      console.log('[StoryArc] summarize-dm:', data.error, 'messageCount:', data.messageCount);
      return null;
    }

    if (!data.summary || data.summary.length < 100) {
      return null;
    }

    // The edge function already saved to dm_conversation_summaries.
    // Parse the structured sections for the return value.
    const parsed = {
      story_arc: data.story_arc || '',
      key_events: data.key_events || '',
      relationship_evolution: data.relationship_evolution || '',
      emotional_state: data.emotional_state || '',
      unresolved_threads: data.unresolved_threads || '',
    };

    // If structured sections weren't returned, parse from summary text
    if (!parsed.story_arc && data.summary) {
      const fallbackParsed = parseSummaryResponse(data.summary);
      Object.assign(parsed, fallbackParsed);
    }

    return parsed;
    }
    // The only live path: ai-proxy, which carries the BYOK key.
    return await generateStoryArcSummaryFallback(conversationId, characterName, targetName, userId, aiProvider, accessToken);
  } catch (err) {
    console.error('Error in generateStoryArcSummary:', err);
    return await generateStoryArcSummaryFallback(conversationId, characterName, targetName, userId, aiProvider, accessToken);
  }
}

/**
 * Fallback: generate story arc summary via ai-proxy (client-side, Gemini/Haiku).
 * Used when the dedicated summarize-dm edge function is unavailable.
 */
async function generateStoryArcSummaryFallback(
  conversationId,
  characterName,
  targetName,
  userId,
  aiProvider,
  accessToken
) {
  const { data: messages, error } = await supabase
    .from('dm_messages')
    .select('sender_type, content, created_at, is_ooc')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
    .limit(500);

  if (error || !messages || messages.length < 20) return null;

  const storyMessages = messages.filter(m => !m.is_ooc && m.content && m.content.length > 10);
  if (storyMessages.length < 15) return null;

  const cutoff = Math.floor(storyMessages.length * 0.8);
  const toSummarize = storyMessages.slice(0, cutoff);

  const conversationText = toSummarize.map(m => {
    const speaker = m.sender_type === 'user' ? targetName : characterName;
    return `${speaker}: ${m.content.substring(0, 300)}`;
  }).join('\n');

  const systemPrompt = `You are summarizing an ongoing roleplay/DM conversation between ${characterName} and ${targetName}.

Create a comprehensive summary that captures:
1. STORY_ARC: The overall narrative arc — what happened from beginning to current point.
2. KEY_EVENTS: The most important plot points and emotional beats (top 5-10).
3. RELATIONSHIP_EVOLUTION: How their relationship changed over the conversation.
4. EMOTIONAL_STATE: Current emotional state of the relationship.
5. UNRESOLVED_THREADS: Open plot threads and unanswered questions.

FORMAT YOUR RESPONSE AS:
STORY_ARC:
(summary)

KEY_EVENTS:
(list)

RELATIONSHIP_EVOLUTION:
(description)

EMOTIONAL_STATE:
(current state)

UNRESOLVED_THREADS:
(list)`;

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
      method: 'POST',
      // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(AI_ABORT_MS),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        provider: aiProvider,
        systemPrompt,
        messages: [{ role: 'user', content: conversationText }],
        maxTokens: 800,
        feature: 'story_arc_summary',
        nsfw: true, // summarizes RP arc which may contain NSFW
        characterName,
      }),
    });

    if (!res.ok) {
      console.error('Story arc summary fallback API error:', res.status);
      return null;
    }

    const data = await res.json();
    const summaryText = data.response || data.content || data.text || '';
    if (!summaryText || summaryText.length < 100) return null;

    const parsed = parseSummaryResponse(summaryText);

    const summaryRow = {
      conversation_id: conversationId,
      user_id: userId,
      summary_text: summaryText,
      messages_covered: cutoff,
      story_arc: parsed.story_arc,
      key_events: parsed.key_events,
      relationship_evolution: parsed.relationship_evolution,
      emotional_state: parsed.emotional_state,
      unresolved_threads: parsed.unresolved_threads,
      updated_at: new Date().toISOString(),
    };

    const { data: existing } = await supabase
      .from('dm_conversation_summaries')
      .select('id')
      .eq('conversation_id', conversationId)
      .maybeSingle();

    if (existing) {
      await supabase.from('dm_conversation_summaries').update(summaryRow).eq('id', existing.id);
    } else {
      await supabase.from('dm_conversation_summaries').insert(summaryRow);
    }

    return parsed;
  } catch (err) {
    console.error('Error in story arc summary fallback:', err);
    return null;
  }
}

/**
 * Parse story arc summary response into sections.
 */
function parseSummaryResponse(text) {
  const sections = {
    story_arc: '',
    key_events: '',
    relationship_evolution: '',
    emotional_state: '',
    unresolved_threads: '',
  };

  const sectionMap = {
    'STORY_ARC': 'story_arc',
    'KEY_EVENTS': 'key_events',
    'RELATIONSHIP_EVOLUTION': 'relationship_evolution',
    'EMOTIONAL_STATE': 'emotional_state',
    'UNRESOLVED_THREADS': 'unresolved_threads',
  };

  let currentSection = null;
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    const headerMatch = trimmed.replace(':', '').replace(/\s+/g, '_').toUpperCase();
    if (sectionMap[headerMatch]) {
      currentSection = sectionMap[headerMatch];
      continue;
    }
    if (currentSection && trimmed) {
      sections[currentSection] += (sections[currentSection] ? '\n' : '') + trimmed;
    }
  }

  // Fallback
  if (!sections.story_arc && text.length > 50) {
    sections.story_arc = text;
  }

  return sections;
}

/**
 * Get existing story arc summary for a conversation.
 */
export async function getStoryArcSummary(conversationId) {
  try {
    const { data, error } = await supabase
      .from('dm_conversation_summaries')
      .select('*')
      .eq('conversation_id', conversationId)
      .maybeSingle();

    if (error || !data) return null;
    return data;
  } catch (err) {
    console.error('Error fetching story arc summary:', err);
    return null;
  }
}

/**
 * Format story arc summary for injection into the system prompt.
 */
export function formatStoryArcForPrompt(summary, characterName, targetName) {
  if (!summary) return '';

  let section = `\nSTORY MEMORY — WHAT HAS HAPPENED IN THIS CONVERSATION SO FAR:\n`;
  section += `(You remember ALL of this. It is your lived experience with ${targetName}. Reference it naturally.)\n`;

  if (summary.story_arc) {
    section += `\nNARRATIVE ARC:\n${summary.story_arc}\n`;
  }

  if (summary.key_events) {
    section += `\nKEY MOMENTS:\n${summary.key_events}\n`;
  }

  if (summary.relationship_evolution) {
    section += `\nHOW YOUR RELATIONSHIP HAS EVOLVED:\n${summary.relationship_evolution}\n`;
  }

  if (summary.emotional_state) {
    section += `\nCURRENT EMOTIONAL STATE:\n${summary.emotional_state}\n`;
  }

  if (summary.unresolved_threads) {
    section += `\nOPEN THREADS (things still unresolved between you):\n${summary.unresolved_threads}\n`;
  }

  return section;
}
